import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import TopNavigation from '../components/TopNavigation';
import { authService } from '@/services/authService';
import type { Entitlements } from '@/services/authService';
import { analytics } from '@/services/analytics';
import { ExchangeIcon, BookOpenIcon, MessageCircleIcon } from '@/components/profile/ProfileIcons';
import DeleteAccountModal from '@/components/profile/DeleteAccountModal';
import LoginRequiredState from '../components/LoginRequiredState';
import MoonIcon from '@/components/icons/MoonIcon';
import SunIcon from '@/components/icons/SunIcon';
import { useConsent } from '@/hooks/useConsent';
import { setAnalyticsConsent } from '@/services/consent';
import { isNativeApp } from '@/utils/platform';
import { isDevSurfaceEnabled } from '@/utils/buildInfo';
import OfflineStorageDevCard, { type OfflineDevContext } from '@/components/profile/OfflineStorageDevCard';
import OfflineListeningCard from '@/components/profile/OfflineListeningCard';
import { billingService } from '@/services/billingService';

interface ProfilePageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  /** The plans page. Shown from the plan card in the native app only, where
   * the subscription is sold in-app; the web has no checkout yet. */
  onNavigateToPricing?: () => void;
  onToggleMenu: () => void;
  isLoading?: boolean;
  /**
   * The chapter currently loaded in the player, for the dev-only offline
   * storage card. Null when nothing is loaded; ignored outside dev builds.
   */
  offlineDevContext?: OfflineDevContext | null;
}

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * How much of the allowance is gone.
 *
 * Only says "this month" when the window actually is a month: the Free tier's
 * 30 minutes are cumulative and never reset today, so claiming otherwise here
 * would be a promise the server doesn't keep.
 */
const usageLabel = (entitlements: Entitlements): string => {
  if (entitlements.limit_minutes === null) {
    return 'Unlimited conversation with Aleph';
  }
  const used = Math.round(entitlements.used_minutes);
  const limit = Math.round(entitlements.limit_minutes);
  const period = entitlements.window === 'month' ? ' this month' : '';
  return `${used} of ${limit} minutes of conversation used${period}`;
};

/**
 * What is happening to the subscription, in the customer's terms.
 *
 * Returns null when there is nothing to say — a free account, or one comped by
 * hand, neither of which has a renewal date or an end.
 */
const renewalLabel = (entitlements: Entitlements): string | null => {
  const when = formatDate(entitlements.current_period_end);
  switch (entitlements.status) {
    case 'active':
      return when ? `Renews ${when}` : null;
    case 'canceled':
      return when ? `Cancelled — your access continues until ${when}` : 'Cancelled';
    case 'past_due':
      return when
        ? `Your last payment didn't go through. Access continues until ${when} while we retry.`
        : "Your last payment didn't go through. We're retrying.";
    case 'expired':
      // Said in full by endedLabel, beside the tier they actually have now.
      return null;
    default:
      return null;
  }
};

// The server stores `premium` for what the store calls Scholar.
const STORED_TIER_NAMES: Record<string, string> = { free: 'Free', member: 'Member', premium: 'Scholar' };

/**
 * The one sentence for a subscription that has run out, replacing the three
 * that used to stack up here ("Free", "Ended …", "Your member subscription is
 * no longer active") and read as contradictions.
 */
const endedLabel = (entitlements: Entitlements): string | null => {
  if (entitlements.stored_tier === entitlements.tier) return null;
  const name = STORED_TIER_NAMES[entitlements.stored_tier] ?? entitlements.stored_tier;
  const when = formatDate(entitlements.current_period_end);
  return when
    ? `Your ${name} subscription ended on ${when}.`
    : `Your ${name} subscription is no longer active.`;
};

const ProfileManagementPage: React.FC<ProfilePageProps> = ({
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateToMyBooks,
  onNavigateHome,
  onToggleMenu,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToPricing,
  isLoading,
  offlineDevContext = null,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout, isLoading: authLoading } = useAuth();
  const isDarkMode = theme === 'dark';

  // Stats
  const [totalBooks, setTotalBooks] = useState(0);
  const [totalConversations, setTotalConversations] = useState(0);
  const [totalExchanges, setTotalExchanges] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Null until the fetch lands, and stays null if it fails — the plan card is
  // omitted rather than guessed at, since a wrong tier here is worse than none.
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);

  // const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';
  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';

  const cardClasses = isDarkMode 
    ? 'bg-gray-800/50 border-gray-700/30'
    : 'bg-gray-50/50 border-gray-300';
  const mutedTextClasses = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  // Reconciled with the account by authService on login and on every user
  // fetch, so this reflects the server's answer rather than just this
  // browser's. On a cold load it can read as off for the moment before that
  // fetch lands; the toggle corrects itself when it does.
  const { status: analyticsConsent } = useConsent();
  const analyticsOn = analyticsConsent === 'granted';

  // Held until auth settles - see the matching effect in MyBooksPage: the
  // seeded user is unconfirmed, and a 401 here reports a 'usage_history' or
  // 'entitlements' Error Shown to Mixpanel.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;
    loadUsageHistory().catch(err => console.error('Error in loadUsageHistory effect:', err));
    loadEntitlements().catch(err => console.error('Error in loadEntitlements effect:', err));
  }, [authLoading, isAuthenticated, user?.id]);

  const loadEntitlements = async () => {
    try {
      setEntitlements(await authService.getEntitlements());
    } catch (error) {
      console.error('Failed to load entitlements:', error);
      analytics.trackErrorShown('entitlements', error, { page: 'profile' });
    }
  };

  // Native: an App Store purchase, restore or out-of-band renewal recorded
  // while this page is open changes the plan card — re-read the server's
  // answer rather than guessing at it.
  useEffect(() => {
    if (!isNativeApp()) return;
    return billingService.onRecorded(() => {
      loadEntitlements().catch(err => console.error('Error reloading entitlements:', err));
    });
  }, []);

  const loadUsageHistory = async () => {
    try {
      const history = await authService.getUserUsageHistory();

      // Process history data for stats
      processStats(history);
    } catch (error) {
      console.error('Failed to load usage history:', error);
      analytics.trackErrorShown('usage_history', error, { page: 'profile' });
    }
  };

  const processStats = (history: any[]) => {
    const uniqueBookIds = new Set();
    let conversationCount = 0;
    let totalTurns = 0;

    history.forEach(entry => {
      if (!entry.book_id) return;
      
      uniqueBookIds.add(entry.book_id);
      conversationCount++;
      totalTurns += entry.turns || 0;
    });

    setTotalBooks(uniqueBookIds.size);
    setTotalConversations(conversationCount);
    setTotalExchanges(totalTurns);
  };

  const handleLogout = async () => {
    try {
      await logout();
      if (onNavigateHome) {
        onNavigateHome();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google': return 'Google';
      case 'facebook': return 'Facebook';
      case 'apple': return 'Apple';
      case 'email': return 'Email';
      default: return provider;
    }
  };

  if (authLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return (
      <LoginRequiredState
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToSignup={onNavigateToSignup}
        isLoading={isLoading}
        message="Please log in to view your profile"
      />
    );
  }

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        showProfileIcon={true}
        isLoading={isLoading}
        activeItem="profile"
      />

      {/* Main Content */}
      <div className="w-full flex flex-col px-4 xs:px-6 sm:px-8 md:px-12 lg:px-16 xl:px-24 pt-6 xs:pt-12 sm:pt-12 pb-[calc(10rem+var(--safe-bottom))]">
        <div className="w-full max-w-6xl mx-auto space-y-6 xs:space-y-8">
          
          {/* Profile Header Card */}
          <div className={`rounded-xl border shadow-md p-6 sm:p-8 ${cardClasses}`}>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 2xs:gap-8">
              {/* Avatar */}
              <div className={`h-24 w-24 sm:h-28 sm:w-28 rounded-full overflow-hidden flex items-center justify-center text-4xl font-medium shrink-0 ${
                isDarkMode
                  ? 'bg-gray-700 text-gray-300'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {user.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user.name} 
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      // Fallback if image fails to load
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement).parentElement;
                      if (parent) {
                        parent.innerText = getInitials(user.name);
                      }
                    }}
                  />
                ) : (
                  getInitials(user.name)
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 text-center sm:text-left w-full">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                  <div>
                    <h1 className="mb-1 text-2xl 2xs:text-3xl font-bold">
                      {user.name}
                    </h1>
                    <p className={`mb-2  ${mutedTextClasses}`}>
                      {user.email}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Signed in with {getProviderDisplayName(user.provider)}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={toggleTheme}
                      aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                      title={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                      className={`flex h-[38px] w-[38px] cursor-pointer items-center justify-center ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      {isDarkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />}
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      disabled={authLoading}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                        isDarkMode
                          ? 'border-red-800 text-red-400 hover:bg-red-900/20'
                          : 'border-red-200 text-red-600 hover:bg-red-50'
                      } ${authLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {authLoading ? 'Signing out...' : 'Sign Out'}
                    </button>
                  </div>
                </div>
                
                <hr className={`my-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                
                {/* Stats Grid */}
                <div className="w-full">
                  <div className={`overflow-hidden ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="flex flex-col items-center 2xs:grid 2xs:grid-cols-[repeat(2,auto)] xs:grid-cols-2 lg:flex lg:flex-row lg:justify-between gap-4 mt-6 2xs:mt-0">
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <BookOpenIcon className={`h-5 w-5 ${mutedTextClasses}`} />
                        <span className="whitespace-nowrap"><strong>{totalBooks}</strong> Books</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <MessageCircleIcon className={`h-4.5 w-4.5 ${mutedTextClasses}`} />
                        <span className="whitespace-nowrap"><strong>{totalConversations}</strong> Conversations</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <ExchangeIcon className={`h-5.5 w-5.5 ${mutedTextClasses}`} />
                        <span className="whitespace-nowrap"><strong>{totalExchanges}</strong> Exchanges</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Card — rendered only once the server has answered. No prices
              here on any platform: they live on the pricing page. The native
              app links to it since 1.2, because that is where the
              subscription is sold; the web has no checkout yet. */}
          {entitlements && (
            <div className={`rounded-xl border shadow-md p-6 sm:p-8 ${cardClasses}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold mb-1">Your plan</h2>
                  <p className="text-2xl font-bold mb-1">{entitlements.tier_name}</p>
                  {renewalLabel(entitlements) && (
                    <p className={`text-sm ${mutedTextClasses}`}>{renewalLabel(entitlements)}</p>
                  )}
                  {/* Only when a lapsed subscription means the effective tier
                      is no longer the one they bought. */}
                  {endedLabel(entitlements) && (
                    <p className={`text-sm ${mutedTextClasses}`}>{endedLabel(entitlements)}</p>
                  )}
                  {isNativeApp() && onNavigateToPricing && (
                    <button
                      type="button"
                      onClick={onNavigateToPricing}
                      className="mt-2 text-sm font-medium text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      {entitlements.tier === 'free' ? 'See plans' : 'Change plan'}
                    </button>
                  )}
                </div>

                <div className="w-full sm:w-64 shrink-0">
                  <p className={`text-sm mb-2 ${mutedTextClasses}`}>{usageLabel(entitlements)}</p>
                  {entitlements.limit_minutes !== null && (
                    <div
                      className={`h-2 w-full rounded-full overflow-hidden ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={Math.round(entitlements.limit_minutes)}
                      aria-valuenow={Math.round(entitlements.used_minutes)}
                      aria-label="Conversation minutes used"
                    >
                      <div
                        className={`h-full rounded-full ${
                          entitlements.remaining_minutes === 0 ? 'bg-red-500' : 'bg-blue-600'
                        }`}
                        style={{
                          width: `${Math.min(
                            100,
                            (entitlements.used_minutes / entitlements.limit_minutes) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Offline listening: native only — there is no store on the web. */}
          {isNativeApp() && (
            <OfflineListeningCard isDarkMode={isDarkMode} cardClasses={cardClasses} mutedTextClasses={mutedTextClasses} />
          )}

          {/* Privacy Card */}
          <div className={`rounded-xl border shadow-md p-6 sm:p-8 ${cardClasses}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold mb-1">Product analytics</h2>
                <p className={`text-sm ${mutedTextClasses}`}>
                  {analyticsOn
                    ? 'We record which pages you open and which books you start, to work out what to improve. No advertising, and no tracking you across other sites.'
                    : 'Off. We are not recording how you use Aleph, from this browser or on our servers. Sign-in security records are kept either way.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsOn}
                aria-label="Product analytics"
                onClick={() => setAnalyticsConsent(!analyticsOn)}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                  analyticsOn
                    ? 'bg-blue-700'
                    : isDarkMode
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    analyticsOn ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Danger Zone Card */}
          <div className={`rounded-xl border shadow-md p-6 sm:p-8 ${cardClasses}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold mb-1">Delete account</h2>
                <p className={`text-sm ${mutedTextClasses}`}>
                  Permanently remove your account and all associated data.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border shrink-0 ${
                  isDarkMode
                    ? 'border-red-800 text-red-400 hover:bg-red-900/20'
                    : 'border-red-200 text-red-600 hover:bg-red-50'
                }`}
              >
                Delete account…
              </button>
            </div>
          </div>

          {/* Dev-only: exercises the offline asset store on a device. Never in a production build. */}
          {isNativeApp() && isDevSurfaceEnabled() && (
            <OfflineStorageDevCard
              context={offlineDevContext}
              isDarkMode={isDarkMode}
              cardClasses={cardClasses}
              mutedTextClasses={mutedTextClasses}
            />
          )}

        </div>
      </div>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        entitlements={entitlements}
        onDeleted={() => {
          setShowDeleteModal(false);
          onNavigateHome?.();
        }}
      />
    </div>
  );
};

export default ProfileManagementPage;
